import React, { createContext, useMemo, useEffect, useContext } from 'react';
import { EventEmitter } from 'events';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

/**
 * This is the wrapper around the Segment client that allows us to queue events until the library
 * has successfully loaded. If events are tracked before the client has loaded, they will be queued
 * up and triggered once it's ready.
 */
var SegmentClient =
/*#__PURE__*/
function () {
  function SegmentClient(options) {
    this.options = options;
    this.emitter = options.emitter;
  }
  /**
   * Set the analytics client. This should only be called once.
   * @param analytics
   */


  var _proto = SegmentClient.prototype;

  _proto.initialize = function initialize(analytics) {
    if (this.client) return;
    var _this$options = this.options,
        _this$options$timeout = _this$options.timeout,
        timeout = _this$options$timeout === void 0 ? 200 : _this$options$timeout,
        _this$options$debug = _this$options.debug,
        debug = _this$options$debug === void 0 ? false : _this$options$debug;
    this.client = analytics;
    this.client.timeout(timeout);
    this.client.debug(debug);
    this.emitter.emit('initialize');
  }
  /**
   * Track a page view
   * @param name The name of the page
   * @param properties Any properties describing this page
   * @param options Segment options
   * @param callback
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
   */
  ;

  _proto.page = function page(event) {
    var _this = this;

    if (event === void 0) {
      event = {};
    }

    return new Promise(function (resolve, reject) {
      var _event = event,
          name = _event.name,
          category = _event.category,
          options = _event.options,
          properties = _event.properties;
      var debug = _this.options.debug;

      if (_this.client) {
        if (debug) {
          console.log('[Segment] Page', event);
        }

        try {
          _this.client.page(category, name, properties, _extends({}, options, {
            context: {
              ip: _this.options.anonymizeIp ? '0.0.0.0' : undefined
            }
          }), function () {
            if (debug) {
              console.log('[Segment] Page succesful', event);
            }

            resolve();
          });
        } catch (error) {
          reject(error);
        }
      } else {
        _this.emitter.once('initialize', function () {
          _this.page(event).then(function () {
            return resolve();
          });
        });
      }
    });
  }
  /**
   * Identify the current user. Whenever this is called, all future track events will
   * be associated with this user.
   * @param userId The uuid of the user
   * @param traits Any extra traits about this person to update in Segment
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
   */
  ;

  _proto.identify = function identify(event) {
    var _this2 = this;

    return new Promise(function (resolve, reject) {
      var userId = event.userId,
          traits = event.traits,
          options = event.options;
      var debug = _this2.options.debug;

      if (_this2.client) {
        if (debug) {
          console.log('[Segment] Identify', event);
        }

        try {
          _this2.client.identify(userId, traits, _extends({}, options, {
            context: {
              ip: _this2.options.anonymizeIp ? '0.0.0.0' : undefined
            }
          }), function () {
            if (debug) {
              console.log('[Segment] Identify succesful', event);
            }

            resolve();
          });
        } catch (error) {
          reject(error);
        }
      } else {
        _this2.emitter.once('initialize', function () {
          _this2.identify(event);
        });
      }
    });
  }
  /**
   * The alias method combines two previously unassociated user identities. Aliasing is generally handled automatically
   * when you identify a user. However, some tools require an explicit alias call.
   *
   * This is an advanced method, but it is required to manage user identities successfully in some of our destinations.
   * Most notably, alias is necessary for properly implementing KISSmetrics and Mixpanel.
   *
   * @param userId The uuid of the user
   * @param traits Any extra traits about this person to update in Segment
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#alias
   */
  ;

  _proto.alias = function alias(event) {
    var _this3 = this;

    return new Promise(function (resolve, reject) {
      var userId = event.userId,
          previousId = event.previousId,
          options = event.options;
      var debug = _this3.options.debug;

      if (_this3.client) {
        if (debug) {
          console.log('[Segment] Alias', event);
        }

        try {
          _this3.client.alias(userId, previousId, options, function () {
            if (debug) {
              console.log('[Segment] Alias succesful', event);
            }

            resolve();
          });
        } catch (error) {
          reject(error);
        }
      } else {
        _this3.emitter.once('initialize', function () {
          _this3.alias(event);
        });
      }
    });
  }
  /**
   * Track an event representing an action by the user on the page.
   * @param event The name of the event to send to Segment. Make sure you follow the style guide for event names
   * @param properties Properties associated with the event
   * @param options
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
   */
  ;

  _proto.track = function track(_track) {
    var _this4 = this;

    return new Promise(function (resolve, reject) {
      var event = _track.event,
          properties = _track.properties,
          options = _track.options;
      var debug = _this4.options.debug;

      if (_this4.client) {
        if (debug) {
          console.log('[Segment] Track', _track);
        }

        try {
          _this4.client.track(event, properties, _extends({}, options, {
            context: {
              ip: _this4.options.anonymizeIp ? '0.0.0.0' : undefined
            }
          }), function () {
            if (debug) {
              console.log('[Segment] Track sucessful', _track);
            }

            resolve();
          });
        } catch (e) {
          reject(e);
        }
      }

      _this4.emitter.once('initialize', function () {
        _this4.track(_track);
      });
    });
  }
  /**
   * This will associate the current user with this group
   * @param name The name of the group to associate the current user with
   * @param traits Traits about this group
   * @param options
   * @param callback
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#group
   */
  ;

  _proto.group = function group(event) {
    var _this5 = this;

    return new Promise(function (resolve, reject) {
      var groupId = event.groupId,
          options = event.options,
          traits = event.traits;
      var debug = _this5.options.debug;

      if (_this5.client) {
        if (debug) {
          console.log('[Segment] Group', event);
        }

        try {
          _this5.client.group(groupId, traits, _extends({}, options, {
            context: {
              ip: _this5.options.anonymizeIp ? '0.0.0.0' : undefined
            }
          }), function () {
            if (debug) {
              console.log('[Segment] Group succesful', event);
            }

            resolve();
          });
        } catch (error) {
          reject(error);
        }
      } else {
        _this5.emitter.once('initialize', function () {
          _this5.group(event);
        });
      }
    });
  }
  /**
   * The ready method allows you to pass in a callback that is called once all enabled destinations load, and once
   * analytics.js finishes initializing.
   * @param callback
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#ready
   */
  ;

  _proto.ready = function ready(callback) {
    var _this6 = this;

    return new Promise(function (resolve) {
      if (_this6.client) {
        _this6.client.ready(function () {
          if (callback && _this6.client) callback(_this6.client);
          resolve();
        });
      } else {
        _this6.emitter.once('initialize', function () {
          _this6.ready(callback);
        });
      }
    });
  }
  /**
   * The global analytics object emits events whenever you call alias, group, identify, track or page.
   * @param method
   * @param callback
   */
  ;

  _proto.on = function on(method, callback) {
    var _this7 = this;

    if (this.client) {
      this.client.on(method, callback);
    } else {
      this.emitter.once('initialize', function () {
        _this7.on(method, callback);
      });
    }
  }
  /**
   * Set the ID for the anonymous
   * @param id
   */
  ;

  _proto.setAnonymousId = function setAnonymousId(id) {
    var _this8 = this;

    return new Promise(function (resolve) {
      if (_this8.client) {
        _this8.client.setAnonymousId(id);
      } else {
        _this8.emitter.once('initialize', function () {
          _this8.setAnonymousId(id);

          resolve();
        });
      }
    });
  };

  return SegmentClient;
}();

// A type of promise-like that resolves synchronously and supports only one observer

const _iteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.iterator || (Symbol.iterator = Symbol("Symbol.iterator"))) : "@@iterator";

const _asyncIteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.asyncIterator || (Symbol.asyncIterator = Symbol("Symbol.asyncIterator"))) : "@@asyncIterator";

// Asynchronously call a function and send errors to recovery continuation
function _catch(body, recover) {
	try {
		var result = body();
	} catch(e) {
		return recover(e);
	}
	if (result && result.then) {
		return result.then(void 0, recover);
	}
	return result;
}

/**
 * This loads the Segment analytics.js snippet onto the page using the API key. This
 * API key maps to the "source" in Segment. This API key will be different depending on the environment.
 * @param apiKey Segment API key
 */
var loadSegmentSnippet = function loadSegmentSnippet(options) {
  try {
    var _temp3 = function _temp3(_result) {
      return _exit2 ? _result : window.analytics;
    };

    var _exit2 = false;
    var apiKey = options.apiKey,
        _options$debug = options.debug,
        debug = _options$debug === void 0 ? false : _options$debug;

    if (typeof window === 'undefined') {
      if (debug) console.log('[Segment] Unable to load analytics.js in a server environment. Skipping.');
      return Promise.resolve(undefined);
    }

    if (typeof window.analytics !== 'undefined') {
      // eslint-disable-next-line no-console
      if (debug) console.log('[Segment] analytics.js already loaded. Using the existing window.analytics.');
      return Promise.resolve(window.analytics);
    }

    var _temp4 = _catch(function () {
      if (debug) console.log("[Segment] Loading analytics.js...");
      if (debug) console.log("[Segment] Using write key: " + apiKey);
      return Promise.resolve(loadScript("https://cdn.segment.com/analytics.js/v1/" + apiKey + "/analytics.min.js")).then(function () {
        if (debug) console.log('[Segment] analytics.js is loaded and ready ✅');
      });
    }, function () {
      console.warn('[Segment] Failed to load analytics.js. No analytics events will be tracked.');
      _exit2 = true;
      return undefined;
    });

    return Promise.resolve(_temp4 && _temp4.then ? _temp4.then(_temp3) : _temp3(_temp4));
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * Load a script into the DOM
 * @param src
 */
var loadScript = function loadScript(src) {
  try {
    if (typeof document === 'undefined') {
      throw new Error('Unable to load script in a server environment');
    }

    return Promise.resolve(new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = src;

      script.onload = function () {
        return resolve();
      };

      script.onerror = reject;
      document.head.appendChild(script);
    }));
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * This is the context object that useAnalytics will be grabbing the client from.
 * When you're trying to mock analytics calls, you should pass a fake value here.
 */

var SegmentContext =
/*#__PURE__*/
createContext(undefined);
/**
 * Load the Segment snippet and add it to the app context. This client will be available before the script
 * has finished loading so that it doesn't block page rendering.
 * @param props SegmentProviderProps
 */

function SegmentProvider(props) {
  var apiKey = props.apiKey,
      children = props.children,
      debug = props.debug,
      timeout = props.timeout,
      anonymizeIp = props.anonymizeIp;
  var emitter = useMemo(function () {
    return new EventEmitter();
  }, []);
  var client = useMemo(function () {
    return new SegmentClient({
      apiKey: apiKey,
      debug: debug,
      timeout: timeout,
      anonymizeIp: anonymizeIp,
      emitter: emitter
    });
  }, [apiKey, debug, timeout, anonymizeIp, emitter]);
  useEffect(function () {
    loadSegmentSnippet({
      apiKey: apiKey,
      debug: debug
    }).then(function (analytics) {
      if (analytics) {
        client.initialize(analytics);
      }
    });
  }, [apiKey, client, debug]);
  return React.createElement(SegmentContext.Provider, {
    value: client
  }, children);
}
/**
 * Return the Segment client added by <SegmentProvider>. This provider must be added higher up in the React tree to
 * be able to use this hook.
 */

function useSegment() {
  var client = useContext(SegmentContext);

  if (!client) {
    throw new Error('The useSegment hook needs <SegmentProvider> to be present higher in the React tree.');
  }

  return client;
}

var EventType;

(function (EventType) {
  EventType["track"] = "track";
  EventType["alias"] = "alias";
  EventType["group"] = "group";
  EventType["identify"] = "identify";
  EventType["page"] = "page";
})(EventType || (EventType = {}));

export { EventType, SegmentClient, SegmentContext, SegmentProvider, useSegment };
//# sourceMappingURL=react-segment-hooks.esm.js.map
